import { Attachment } from "@/types/attachment";

export type MediaKind = "image" | "video" | "audio" | "other";

const EXTENSION_MAP: Record<Exclude<MediaKind, "other">, Set<string>> = {
  image: new Set([
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "webp",
    "svg",
    "tif",
    "tiff",
    "heic",
    "heif",
    "avif",
  ]),
  video: new Set([
    "mp4",
    "m4v",
    "mov",
    "avi",
    "wmv",
    "flv",
    "webm",
    "mkv",
    "3gp",
    "3g2",
  ]),
  audio: new Set([
    "mp3",
    "wav",
    "aac",
    "m4a",
    "flac",
    "ogg",
    "oga",
    "opus",
    "wma",
    "aiff",
    "aif",
  ]),
};

function inferKindFromMime(mime: string): MediaKind {
  const normalized = mime.toLowerCase();
  if (normalized.startsWith("image/")) {
    return "image";
  }
  if (normalized.startsWith("video/")) {
    return "video";
  }
  if (normalized.startsWith("audio/")) {
    return "audio";
  }
  if (normalized === "application/ogg" || normalized === "application/x-ogg") {
    return "audio";
  }
  if (normalized === "application/mp4") {
    return "video";
  }
  return "other";
}

function extractExtension(source: string): string {
  const dotIndex = source.lastIndexOf(".");
  if (dotIndex < 0) {
    return "";
  }

  return source.slice(dotIndex + 1).toLowerCase();
}

function inferKindFromExtension(extension: string): MediaKind {
  const ext = extension.toLowerCase();
  if (EXTENSION_MAP.image.has(ext)) {
    return "image";
  }
  if (EXTENSION_MAP.video.has(ext)) {
    return "video";
  }
  if (EXTENSION_MAP.audio.has(ext)) {
    return "audio";
  }
  return "other";
}

export function inferMediaKind(attachment: Attachment): MediaKind {
  if (attachment.type === "file") {
    return inferKindFromMime(attachment.file.type);
  } else if (attachment.type === "url") {
    return inferKindFromExtension(extractExtension(attachment.url));
  } else {
    return "other";
  }
}

// 检查 URL 是否跨域
function isCrossOrigin(url: string): boolean {
  try {
    const targetUrl = new URL(url);
    const currentOrigin = window.location.origin;
    return targetUrl.origin !== currentOrigin;
  } catch {
    return false;
  }
}

export async function downloadFile(url: string, filename?: string) {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    // 可选：从 URL 猜文件名
    if (!filename) {
      try {
        const u = new URL(url);
        filename = decodeURIComponent(
          u.pathname.split("/").pop() || "download"
        );
      } catch {
        filename = "download";
      }
    }

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);

    // 给一个下载预期
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, 1000);
    });
  } catch (error) {
    console.error("download file error:", error);
    throw error;
  }
}
export async function selectFile(accept?: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = false;
    input.accept =
      accept || "image/jpeg,image/png,image/webp,image/jpg,video/mp4";
    input.style.display = "none";
    document.body.appendChild(input);

    const handleFileSelect = async (e: Event) => {
      try {
        const files = (e.target as HTMLInputElement).files;
        if (files && files.length > 0) {
          const f = files[0];
          resolve(f);
        } else {
          reject(new Error("No file selected"));
        }
      } catch (error) {
        reject(error);
      } finally {
        const parent = input.parentNode;
        if (parent) {
          parent.removeChild(input);
        }
      }
    };
    input.addEventListener("change", handleFileSelect);
    input.click();
  });
}

/**
 * 将 Data URL 转换为 File 对象
 */
export async function dataUrlToFile(
  dataUrl: string,
  name: string,
  type: string,
  lastModified: number
): Promise<File> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], name, { type, lastModified });
}
